import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { OMCS, OMC_LABELS, type Omc } from '@shared/domain';
import { checkTaxIds } from '@shared/taxId';
import { api } from '../../api';
import { Badge, ErrorBox, Field, Loading, useBusy } from '../../ui';
import { INDIAN_STATES } from './Onboarding';

interface Identifier { value: string; status: 'UNVERIFIED' | 'VERIFIED' | 'REJECTED'; rejectedReason?: string }
interface Profile {
  side: 'BUYER' | 'SUPPLIER';
  name: string;
  legalType: string;
  address: { line1: string; city: string; state: string; pincode: string };
  gstin?: Identifier;
  pan?: Identifier;
  contactPhoneMasked: string;
  contactPhoneVerified: boolean;
  locations: { _id: string; name: string; code: string; omc?: Omc }[];
}

function IdStatus({ id }: { id?: Identifier }) {
  if (!id) return null;
  if (id.status === 'VERIFIED') return <Badge tone="ok">checked</Badge>;
  if (id.status === 'REJECTED') return <Badge tone="bad">could not be confirmed{id.rejectedReason ? `: ${id.rejectedReason}` : ''}. Please correct it</Badge>;
  return <Badge tone="warn">not checked yet</Badge>;
}

/** The owner's view of their own business details, with GSTIN and PAN corrections. */
export function BusinessProfile() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['org-profile'], queryFn: () => api.get<Profile>('/api/org/profile') });
  const [f, setF] = useState({ name: '', legalType: 'OTHER', line1: '', city: '', state: '', pincode: '', gstin: '', pan: '' });
  const [saved, setSaved] = useState(false);
  const { busy, error, run } = useBusy();
  useEffect(() => {
    const p = q.data;
    if (p) setF({ name: p.name, legalType: p.legalType, line1: p.address.line1, city: p.address.city, state: p.address.state, pincode: p.address.pincode, gstin: p.gstin?.value ?? '', pan: p.pan?.value ?? '' });
  }, [q.data]);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  const p = q.data;
  const pump = p.side === 'SUPPLIER';
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => { setSaved(false); setF({ ...f, [k]: e.target.value }); };
  return (
    <div className="stack">
      <h1>Business details</h1>
      <form className="card stack" onSubmit={(e) => {
        e.preventDefault();
        void run(async () => {
          const problem = checkTaxIds({ gstin: f.gstin, pan: f.pan, state: f.state }, { gstinRequired: pump });
          if (problem) throw new Error(problem);
          await api.put('/api/org/profile', { name: f.name.trim(), legalType: f.legalType, address: { line1: f.line1, city: f.city, state: f.state, pincode: f.pincode }, gstin: f.gstin.trim() || undefined, pan: f.pan.trim() });
          await qc.invalidateQueries({ queryKey: ['org-profile'] });
          setSaved(true);
        });
      }}>
        <Field label="Business name"><input value={f.name} onChange={set('name')} required minLength={2} /></Field>
        <Field label="Type">
          <select value={f.legalType} onChange={set('legalType')}>
            <option value="SOLE_PROPRIETOR">Proprietor (one owner)</option>
            <option value="PARTNERSHIP">Partnership</option>
            <option value="COMPANY">Private / public company</option>
            <option value="LLP">LLP</option>
            <option value="OTHER">Other</option>
          </select>
        </Field>
        <Field label="Address"><input value={f.line1} onChange={set('line1')} required minLength={2} /></Field>
        <div className="grid three">
          <Field label="City"><input value={f.city} onChange={set('city')} required minLength={2} /></Field>
          <Field label="State">
            <select value={f.state} onChange={set('state')} required>
              {!INDIAN_STATES.includes(f.state) ? <option value={f.state}>{f.state || 'Choose…'}</option> : null}
              {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Pincode"><input inputMode="numeric" pattern="\d{6}" maxLength={6} value={f.pincode} onChange={set('pincode')} required /></Field>
        </div>
        <div className="grid two">
          <Field label={pump ? 'GSTIN' : 'GSTIN (if registered)'} hint="15 characters."><input value={f.gstin} onChange={set('gstin')} maxLength={15} required={pump} /></Field>
          <Field label="PAN" hint="Business PAN, 10 characters."><input value={f.pan} onChange={set('pan')} maxLength={10} required /></Field>
        </div>
        <p className="muted">GSTIN <IdStatus id={p.gstin} /> · PAN <IdStatus id={p.pan} /></p>
        <p className="muted">Changing GSTIN or PAN marks it “not checked” until SuperIndent support confirms it. Your business keeps working meanwhile. Mobile on file: {p.contactPhoneMasked}{p.contactPhoneVerified ? ' (confirmed)' : ''}.</p>
        <ErrorBox error={error} />
        {saved ? <div className="alert ok">Saved.</div> : null}
        <button className="btn primary" disabled={busy}>{busy ? 'Saving…' : 'Save details'}</button>
      </form>
      {pump ? (
        <div className="card stack">
          <h2>Oil company</h2>
          {p.locations.map((l) => (
            <Field key={l._id} label={`${l.name} (${l.code})`}>
              <select value={l.omc ?? ''} onChange={(e) => void run(async () => { await api.put(`/api/locations/${l._id}/omc`, { omc: e.target.value }); await qc.invalidateQueries({ queryKey: ['org-profile'] }); })}>
                <option value="" disabled>Choose…</option>
                {OMCS.map((o) => <option key={o} value={o}>{OMC_LABELS[o]}</option>)}
              </select>
            </Field>
          ))}
        </div>
      ) : null}
    </div>
  );
}
